import { cn } from '@/utils/cn';
import type { AlarmRollupBannerScope, SeverityCounts } from './types';

interface Props {
  scope: AlarmRollupBannerScope;
  name: string;
  counts: SeverityCounts;
  className?: string;
}

export function AlarmRollupBanner({ scope, name, counts, className }: Props) {
  const isQuiet = counts.critical === 0 && counts.warning === 0 && counts.info === 0;
  const borderColor = isQuiet
    ? 'border-ems-green/40 border-l-ems-green'
    : counts.critical > 0
      ? 'border-ems-red/40 border-l-ems-red'
      : counts.warning > 0
        ? 'border-ems-amber/40 border-l-ems-amber'
        : 'border-ems-blue/40 border-l-ems-blue';

  const padding = scope === 'site' ? 'py-3 px-4' : 'py-2 px-3';
  const numSize = scope === 'site' ? 'text-2xl' : 'text-base';
  const nameSize = scope === 'site' ? 'text-base font-bold' : 'text-sm font-semibold';

  return (
    // role="region" (not "banner"): this renders once per scope — a site rollup
    // plus one per block — so "banner" (reserved for the single page-level
    // banner) would pollute the a11y landmark tree with 10+ duplicate landmarks.
    // "region" requires an accessible name, supplied by aria-label. (#240)
    <div
      role="region"
      aria-label={`${scope === 'site' ? 'Site' : 'Block'} alarm rollup: ${name}`}
      data-scope={scope}
      data-quiet={isQuiet ? 'true' : 'false'}
      className={cn(
        'flex items-center gap-4 rounded border border-l-[3px] bg-ems-bg-card',
        borderColor,
        padding,
        className,
      )}
    >
      <div className="min-w-0">
        <div className="text-[10px] text-ems-text-muted uppercase tracking-wider">
          {scope === 'site' ? 'Site' : 'Block'}
        </div>
        <div className={cn(nameSize, 'text-ems-text-primary truncate')}>{name}</div>
        {isQuiet && (
          <div className="text-xs text-ems-green mt-0.5">All systems nominal</div>
        )}
      </div>
      <div className="ml-auto flex gap-3">
        <Stat label="Critical" value={counts.critical} color="text-ems-red" size={numSize} />
        <Stat label="Warning" value={counts.warning} color="text-ems-amber" size={numSize} />
        <Stat label="Info" value={counts.info} color="text-ems-blue" size={numSize} />
        <Stat label="Acked" value={counts.acked} color="text-ems-text-muted" size={numSize} />
        <Stat label="Stale" value={counts.stale} color="text-ems-text-muted" size={numSize} />
      </div>
    </div>
  );
}

interface StatProps {
  label: string;
  value: number;
  color: string;
  size: string;
}

function Stat({ label, value, color, size }: StatProps) {
  return (
    <div className="text-center min-w-[55px]" data-stat={label.toLowerCase()}>
      <div className={cn('font-bold leading-none', color, size)}>{value}</div>
      <div className="text-[9px] text-ems-text-muted uppercase tracking-wider mt-0.5">
        {label}
      </div>
    </div>
  );
}
